import { Flex } from "@chakra-ui/react";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import CopyableCode from "components/Shared/CopyableCode";
import { NA } from "components/Shared/EmptyPlaceholder";
import LabeledValue from "components/Shared/LabeledValue";
import Link from "components/Shared/Link";
import { BaseDistributionCenterName } from "components/Shared/OrderingPlatformDirectory/DistributionCenters/DistributionCenterName";
import TagList from "components/Shared/TagList";
import { makeFormatCurrency } from "utils/formatting";
import { customerWarehousePage } from "utils/links";

const formatCurrency = makeFormatCurrency({ minimumFractionDigits: 2 });

const fragment = gql(/* GraphQL */ `
  fragment ViewSkuBrandOrderingPlatform on SkuBrandOrderingPlatform {
    id
    itemCode
    wholesaleUnitPrice
    orderingPlatform {
      id
      distributionCenters(
        filter: { name: { isNull: false }, isArchived: { equalTo: false } }
      ) {
        totalCount
      }
    }
    skuBrandDistributionCentersList(
      orderBy: DISTRIBUTION_CENTER_NAME_ASC
      filter: {
        brandDistributionCenter: {
          isArchived: { equalTo: false }
          distributionCenter: {
            name: { isNull: false }
            isArchived: { equalTo: false }
          }
        }
      }
    ) {
      id
      distributionCenter {
        id
        name
        ...DistributionCenterBaseNameFragment
      }
    }
  }
`);

type Props = {
  skuBrandOrderingPlatform: FragmentType<typeof fragment>;
};

export default function SkuOrderingPlatformView({
  skuBrandOrderingPlatform: skuBrandOrderingPlatformFragment,
}: Props) {
  const sbop = getFragmentData(fragment, skuBrandOrderingPlatformFragment);
  const op = sbop.orderingPlatform;
  return (
    <>
      <Flex>
        <LabeledValue
          label="Item Code"
          value={sbop.itemCode && <CopyableCode code={sbop.itemCode} />}
          pb={0}
        />

        <LabeledValue
          label="Wholesale Unit Price"
          value={
            sbop.wholesaleUnitPrice
              ? formatCurrency(parseFloat(sbop.wholesaleUnitPrice))
              : undefined
          }
          pb={0}
        />
      </Flex>

      {!!op?.distributionCenters.totalCount && (
        <LabeledValue
          label="Distribution Centers"
          value={
            sbop.skuBrandDistributionCentersList?.length ? (
              <TagList
                items={sbop.skuBrandDistributionCentersList}
                getKey={(sbdc) => sbdc.id}
                getLabel={(sbdc) => (
                  <Link
                    href={customerWarehousePage(
                      op.id,
                      sbdc.distributionCenter?.id,
                    )}
                  >
                    <BaseDistributionCenterName
                      distributionCenter={sbdc.distributionCenter}
                    />
                  </Link>
                )}
                variant="outline"
              />
            ) : (
              <NA />
            )
          }
          pb={0}
        />
      )}
    </>
  );
}
